/**
 * Shared plumbing for the CFO tabs on the Revenue page: one data
 * hook + the small presentational atoms every tab uses, including
 * the ⓘ InfoTip that surfaces definitions + calculation methods
 * from metricDefs.js on hover.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Loader2, AlertTriangle, Info } from "lucide-react";
import { METRIC_DEFS } from "./metricDefs";

const API = process.env.REACT_APP_BACKEND_URL;

export function useCfoData(path) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  useEffect(() => {
    let live = true;
    setLoading(true);
    axios.get(`${API}/api/proposals/pipeline/${path}`)
      .then((r) => { if (live) { setData(r.data); setError(""); } })
      .catch((e) => { if (live) setError(e.response?.data?.detail || "Could not load this analysis."); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [path]);
  return { data, loading, error };
}

/**
 * Hover ⓘ - looks up its definition in metricDefs.js. Rendered with
 * position:fixed so it escapes overflow-hidden cards and scrollable
 * tables; flips below the icon when too close to the viewport top.
 */
export function InfoTip({ id, className = "", extra }) {
  const def = METRIC_DEFS[id];
  const [pos, setPos] = useState(null);
  const ref = useRef(null);
  if (!def) return null;
  const show = () => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const below = r.top < 200;
    setPos({
      x: Math.min(Math.max(r.left + r.width / 2, 176), window.innerWidth - 176),
      y: below ? r.bottom + 8 : r.top - 8,
      below,
    });
  };
  return (
    <span className={`inline-flex align-middle ${className}`}>
      <span
        ref={ref}
        onMouseEnter={show}
        onMouseLeave={() => setPos(null)}
        data-testid={`info-${id}`}
        className="inline-flex cursor-help p-0.5 -m-0.5"
      >
        <Info className="w-3 h-3 text-stone-300 hover:text-[#1B4F8A] transition-colors" />
      </span>
      {pos && (
        <span
          className={`fixed z-[80] w-[21rem] max-w-[90vw] -translate-x-1/2 pointer-events-none ${pos.below ? "" : "-translate-y-full"}`}
          style={{ left: pos.x, top: pos.y }}
          data-testid={`info-pop-${id}`}
        >
          <span className="block rounded-xl border border-stone-200 bg-white shadow-xl p-3.5 text-left normal-case tracking-normal whitespace-normal">
            <span className="block text-[11px] font-semibold text-slate-900">{def.title}</span>
            <span className="block text-[11px] font-normal text-stone-600 mt-1 leading-snug">{def.what}</span>
            <span className="block text-[10px] font-normal text-stone-500 mt-1.5 leading-snug">
              <span className="font-semibold text-[#1B4F8A]">Calculation · </span>{def.how}
            </span>
            {extra && extra.length > 0 && (
              <span className="block mt-2 pt-2 border-t border-stone-100">
                {extra.map((line) => (
                  <span key={line} className="block text-[10px] font-mono font-normal text-stone-600 leading-relaxed">
                    {line}
                  </span>
                ))}
              </span>
            )}
            {def.source && (
              <span className="block text-[9px] font-mono font-normal text-stone-400 mt-1.5 leading-snug">
                Source · {def.source}
              </span>
            )}
          </span>
        </span>
      )}
    </span>
  );
}

export function TabState({ loading, error, testid }) {
  if (loading) {
    return (
      <div className="flex items-center gap-2 text-stone-500 text-sm py-8" data-testid={`${testid}-loading`}>
        <Loader2 className="w-4 h-4 animate-spin" />
        Crunching the ledger…
      </div>
    );
  }
  if (error) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 flex items-start gap-2" data-testid={`${testid}-error`}>
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
        <span>{error}</span>
      </div>
    );
  }
  return null;
}

export function KpiStrip({ cards }) {
  const cols = { 1: "md:grid-cols-1", 2: "md:grid-cols-2", 3: "md:grid-cols-3", 4: "md:grid-cols-4", 5: "md:grid-cols-5", 6: "md:grid-cols-6" }[Math.min(cards.length, 6)];
  return (
    <div className={`grid grid-cols-2 ${cols} gap-px bg-stone-100 rounded-2xl overflow-hidden border border-stone-200`}>
      {cards.map((c) => (
        <div key={c.label} className="bg-white px-4 py-3" data-testid={c.testid}>
          <p className="text-[10px] font-mono uppercase tracking-wider text-stone-500 mb-1 min-h-[30px]">
            {c.label}
            {c.info && <InfoTip id={c.info} className="ml-1" extra={c.infoExtra} />}
          </p>
          <p className={`text-2xl font-medium tabular-nums leading-none ${c.tone || "text-slate-900"}`}>{c.value}</p>
          {c.sub && <p className={`text-[10px] font-mono mt-1 ${c.subTone || "text-stone-400"}`}>{c.sub}</p>}
        </div>
      ))}
    </div>
  );
}

export function Card({ children, testid, className = "" }) {
  return (
    <section data-testid={testid} className={`bg-white rounded-2xl border border-stone-200 p-5 ${className}`}>
      {children}
    </section>
  );
}

export function SourceBadge({ kind }) {
  const map = {
    wip: { text: "WiP ledger", cls: "text-teal-800 bg-teal-50 border-teal-200" },
    pipeline: { text: "SoW pipeline · indicative", cls: "text-violet-800 bg-violet-50 border-violet-200" },
    joined: { text: "WiP £ + SoW dates", cls: "text-indigo-800 bg-indigo-50 border-indigo-200" },
    integrity: { text: "WiP vs SoW cross-check", cls: "text-amber-800 bg-amber-50 border-amber-200" },
  };
  const b = map[kind] || map.wip;
  return (
    <span className={`inline-block text-[9px] font-mono uppercase px-1.5 py-0.5 rounded border ${b.cls}`}>
      {b.text}
    </span>
  );
}
